import { useEffect, useState, ReactNode } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { AnimatedExplainer } from "@/components/AnimatedExplainer";
import {
  PIShell,
  Drill,
  PIInsight,
  PINoteImage,
  PracticeLadder,
} from "@/components/pi/PIShell";
import { PianoDiagram, KeyHighlight } from "@/components/PianoDiagram";
import { LessonMidi } from "@/components/MidiPlayer";
import { KeyPicker } from "@/components/KeyPicker";
import { KeyProvider, useKeyOrDefault } from "@/theory/key";
import { midiToPitchName, tonicMidiForPc } from "@/theory/melody";
import { kickAudio, playSequence } from "@/audio/piano";

/**
 * Piano Intermediate · Lesson 19 — Pivot Thirds (Major Formation).
 *
 * One pivot note stays put (root or fifth — Sa or Pa), and diatonic third
 * pairs float around it. The pivot can sit on the pinky (so the thirds
 * float to the LEFT) or the thumb (so they float to the RIGHT). Played as
 * triplets — the pivot is one of the three notes of every triplet, the
 * third pair fills in the other two.
 *
 * Bespoke visual: a Triplet Cycle. Three slots — L, M, H — animate the
 * triplet pulse. The anchor slot is gold and never moves; the other two
 * slots cycle through the available third pairs.
 */

type PivotFinger = "pinky" | "thumb";
type PivotDegree = 1 | 5;

export default function PianoIntermediate19() {
  const { lesson, course, accent } = findLesson("piano-intermediate", "19");
  return (
    <LessonShell
      course={course}
      courseSlug="piano-intermediate"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <AnimatedExplainer
        src="/animations/piano-intermediate/PI11-Pivot-Thirds-Animated.mp4"
        caption={`Pinky holds the root G · the third pairs G–B, A–C, B–D, C–E, D–F♯ float below it, in triplets`}
        hint={`≈ 22 sec · the pivot technique`}
      />
      <KeyProvider defaultTonic="G" availableQualities={["major"]}>
        <PIShell
          title="Pivot Thirds — Major Formation"
          exerciseGoal="One note stays still. The third pairs float around it. Played in triplets, both hands. Sounds like music; it's actually a finger drill."
          bpm={92}
          difficulty="Advanced"
        >
          <Body />
        </PIShell>
      </KeyProvider>
    </LessonShell>
  );
}

function Body() {
  const k = useKeyOrDefault();
  const tonicMidi = tonicMidiForPc(k.tonicPc);
  const [pivotFinger, setPivotFinger] = useState<PivotFinger>("pinky");
  const [pivotDegree, setPivotDegree] = useState<PivotDegree>(1);
  const [running, setRunning] = useState(false);
  const [tripletStep, setTripletStep] = useState(-1);

  // Build the seven diatonic third pairs of the current major scale
  const scaleSemis = [0, 2, 4, 5, 7, 9, 11]; // major
  type Pair = { lower: number; upper: number; type: "Major" | "Minor" };
  const pairs: Pair[] = [];
  for (let i = 0; i < 7; i++) {
    const lower = scaleSemis[i];
    const upper = scaleSemis[(i + 2) % 7] + (i + 2 >= 7 ? 12 : 0);
    pairs.push({ lower, upper, type: upper - lower === 4 ? "Major" : "Minor" });
  }

  // Pivot semitones from tonic (0 for root, 7 for fifth)
  const pivotSemis = pivotDegree === 1 ? 0 : 7;
  const pivotPc = ((k.tonicPc + pivotSemis) % 12 + 12) % 12;

  // Where the right hand sits. The root anchor is the tonic above middle C (G4 in
  // G); the fifth anchor is the fifth BELOW it (D4) — "I could play it lower, play
  // it higher. I'm just going to choose it lower" (the video). Tonic-relative
  // placement put the right hand down around G2–G3, on top of the left-hand bass.
  const rhRoot = tonicMidi + 12;
  const pivotMidi = pivotDegree === 1 ? rhRoot : rhRoot - 5;

  /** A pair placed against the pivot: below it for the pinky, above it for the thumb. */
  const place = (pair: { lower: number; upper: number }): [number, number] => {
    let lo = tonicMidi + pair.lower;
    let hi = tonicMidi + pair.upper;
    if (pivotFinger === "pinky") {
      while (hi >= pivotMidi) { lo -= 12; hi -= 12; }
      while (hi + 12 < pivotMidi) { lo += 12; hi += 12; }
    } else {
      while (lo <= pivotMidi) { lo += 12; hi += 12; }
      while (lo - 12 > pivotMidi) { lo -= 12; hi -= 12; }
    }
    return [lo, hi];
  };

  // Which pairs fit: the whole triplet must sit inside one octave of the pivot —
  // "your hand has probably the stretch of an octave. I don't think you'd want to
  // struggle going further than that." Each anchor gets the five pairs that fit,
  // walked from the lowest up.
  const usablePairs = pairs
    .filter((p) => {
      const [lo, hi] = place(p);
      return pivotFinger === "pinky" ? pivotMidi - lo <= 12 : hi - pivotMidi <= 12;
    })
    .sort((a, b) => place(a)[0] - place(b)[0]);
  const [pairIdx, setPairIdx] = useState(0);

  // Animate triplet pulse
  useEffect(() => {
    if (!running) return;
    const id = window.setInterval(() => {
      setTripletStep((s) => {
        const next = (s + 1) % 3;
        // Advance the third pair every full triplet
        if (next === 0) setPairIdx((p) => (p + 1) % usablePairs.length);
        return next;
      });
    }, 60_000 / 92 / 3); // triplet eighth at 92bpm
    return () => window.clearInterval(id);
  }, [running, usablePairs.length]);

  const playLoop = async () => {
    kickAudio();
    setRunning(true);
    setTripletStep(-1);
    setPairIdx(0);

    const tripletSec = 60 / 92 / 3; // duration of one triplet eighth
    const events: { time: number; notes: string[]; duration: string }[] = [];
    let t = 0;

    // One triplet per usable pair, walking up through them
    for (let cycle = 0; cycle < usablePairs.length; cycle++) {
      const pair = usablePairs[cycle % usablePairs.length];
      // Placed against the PIVOT: pinky pivot = the pivot is the top note and
      // the thirds float below it; thumb pivot = the pivot is the bottom note
      // and the thirds float above. Either way the triplet ascends (L–M–H).
      const [lo, hi] = place(pair);
      const triplet = pivotFinger === "pinky"
        ? [lo, hi, pivotMidi]
        : [pivotMidi, lo, hi];
      for (let i = 0; i < 3; i++) {
        events.push({
          time: t,
          notes: [midiToPitchName(triplet[i], k.useFlats)],
          duration: "8t",
        });
        t += tripletSec;
      }
    }
    // LH bass: a half-note pulse on the anchor's note, low — the root, or the
    // fifth when the fifth is the anchor ("supported with a nice D", the video).
    const loopDur = events[events.length - 1].time + tripletSec;
    let bassMidi = pivotMidi - 24;
    while (bassMidi > 50) bassMidi -= 12;
    while (bassMidi < 36) bassMidi += 12;
    let bt = 0;
    while (bt < loopDur) {
      events.push({
        time: bt,
        notes: [midiToPitchName(bassMidi, k.useFlats)],
        duration: "2n",
        velocity: 0.55,
      } as any);
      bt += 60 / 92 * 2;
    }
    await playSequence(events, 92);
    setTimeout(() => setRunning(false), 150);
  };

  const stop = () => {
    setRunning(false);
    setTripletStep(-1);
  };

  // Master keyboard: the pivot lit gold, scale degrees of current pair lit cyan
  const masterHighlights: KeyHighlight[] = [
    { pc: pivotPc, tone: "root", label: pivotDegree === 1 ? "PIVOT (Sa)" : "PIVOT (Pa)" },
    ...(usablePairs[pairIdx]
      ? [
          { pc: ((k.tonicPc + usablePairs[pairIdx].lower) % 12 + 12) % 12, tone: "voice" as const, label: "3rd" },
          { pc: ((k.tonicPc + usablePairs[pairIdx].upper) % 12 + 12) % 12, tone: "voice" as const, label: "3rd" },
        ]
      : []),
  ];

  return (
    <>
      <StudyDeck>
        <KeyPicker />
        <PianoDiagram
          highlights={masterHighlights}
          octaves={2}
          startOctave={3}
          playable
          ariaLabel="Pivot note + the current third pair"
        />
        <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-2">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[12px] text-cyan-300/70">Pivot finger:</span>
            {(["pinky", "thumb"] as PivotFinger[]).map((f) => (
              <button
                key={f}
                onClick={() => setPivotFinger(f)}
                className={`rounded-md border px-2 py-1 font-mono text-[11px] font-bold uppercase ${
                  pivotFinger === f
                    ? "border-amber-400/60 bg-amber-500/15 text-amber-200"
                    : "border-cyan-400/30 bg-cyan-500/5 text-cyan-200 hover:bg-cyan-500/15"
                }`}
              >
                {f === "pinky" ? "Pinky (5)" : "Thumb (1)"}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[12px] text-cyan-300/70">Pivot note:</span>
            {([1, 5] as PivotDegree[]).map((d) => (
              <button
                key={d}
                onClick={() => setPivotDegree(d)}
                className={`rounded-md border px-2 py-1 font-mono text-[11px] font-bold uppercase ${
                  pivotDegree === d
                    ? "border-amber-400/60 bg-amber-500/15 text-amber-200"
                    : "border-cyan-400/30 bg-cyan-500/5 text-cyan-200 hover:bg-cyan-500/15"
                }`}
              >
                {d === 1 ? `Root (${k.nameForPc(k.tonicPc)})` : `Fifth (${k.nameForPc(k.pcFromDegree(5))})`}
              </button>
            ))}
          </div>
        </div>
      </StudyDeck>

      <FadeIn>
        <Drill number="1" label="The big idea" title="Two ingredients: anchors and thirds">
          <p>
            <strong>Pivot</strong> = an anchor note that stays put.
            <strong> Thirds</strong> = the seven diatonic third pairs of the
            current scale (1↔3, 2↔4, 3↔5 …). Combine them: keep one
            note as a pivot in your hand; let the third pairs float around
            it. Played as triplets, this becomes a finger drill that
            sounds like genuine music.
          </p>
          <PIQuote attribution="Jason · lesson video">
            "Those anchors are going to be either the left of the thirds which
            you're going to play or it's going to be the right of the thirds
            which you're going to play and the thirds are going to be floated
            around."
          </PIQuote>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="2" label="Two pivots, two finger choices" title="Pinky pivots — thirds float left">
          <p>
            When the <strong>pinky</strong> anchors a high pivot note (root
            or fifth), every third pair has to sit <em>below</em> it — the
            other fingers float to the LEFT of the pinky. When the
            <strong> thumb</strong> anchors a low pivot, the thirds float to
            the RIGHT. Use the buttons in the study deck above to switch
            between the four configurations.
          </p>
          <p>
            <strong>The pivot is one note of every triplet.</strong> The
            third pair fills in the other two. So your hand never plays
            more than three notes between attacks.
          </p>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="3" label="The triplet" title="L–M–H, the pivot is one of the three">
          <TripletCycle
            step={running ? tripletStep : -1}
            pivotFinger={pivotFinger}
            pair={usablePairs[pairIdx]}
            pivotDegree={pivotDegree}
            nameForPc={k.nameForPc}
            tonicPc={k.tonicPc}
          />
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              onClick={playLoop}
              disabled={running}
              className="rounded-lg bg-amber-400 px-4 py-2 text-sm font-bold text-zinc-900 hover:bg-amber-300 disabled:opacity-50"
            >
              ▶ Run the cycle in {k.tonicName}
            </button>
            <button
              onClick={stop}
              disabled={!running}
              className="rounded-lg border border-cyan-400/40 bg-cyan-500/10 px-3 py-2 text-sm font-semibold text-cyan-200 hover:bg-cyan-500/20 disabled:opacity-40"
            >
              ■ Stop
            </button>
          </div>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="4" label="The hand limit" title="Stretch — but only as far as your biology allows">
          <p>
            How far you can float depends on your hand. An octave stretch
            is the <em>de facto</em> standard for a piano hand; some hands
            reach a tenth, others six or seven keys. Don't strain. Pick
            third pairs that fit comfortably between your pivoting finger
            and the rest of your hand.
          </p>
          <PIQuote attribution="Jason · lesson video">
            "Now how far do you go? Well as far as your biology supports. So
            your hand has probably the stretch of an octave. I don't think
            you'd want to struggle going further than that."
          </PIQuote>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="5" label="Left hand" title="A pulse, a bass walk, or modal colour">
          <p>
            The right hand is busy doing pivots and thirds; the left should
            stay simple. Suggested:
          </p>
          <ul className="my-3 space-y-1 text-[14px] text-cyan-50/95">
            <li>· The root as a simple pulse, to start with.</li>
            <li>· Under the fifth anchor, put the fifth in the bass — Jason hears a modal, Mixolydian flavour.</li>
            <li>· Then change the bass — not too swiftly — based on the music the thirds are making.</li>
          </ul>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="6" label="Play along" title="The pivot loop">
          <LessonMidi
            courseSlug="piano-intermediate"
            order="19"
            label={`Pivot thirds · ${k.tonicName} major`}
            originalBpm={92}
          />
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="7" label="Practice ladder" title="Sixteen minutes, four rungs">
          <PracticeLadder
            intro={
              <>
                Lock the triplet first. The pivot finger has to stay still
                while the others move — that's the whole drill. Once the
                triplet feels effortless, swap pivots.
              </>
            }
            rungs={[
              { title: "Pinky on root, thirds below", minutes: 4, accent: "amber",   detail: "Pinky on the tonic, high. Float third pairs underneath. Triplets at slow tempo." },
              { title: "Pinky on fifth, thirds below", minutes: 4, accent: "cyan",    detail: "Move the pinky to the 5th. Notice how the modal flavour shifts (now Mixolydian-tinted)." },
              { title: "Thumb on root, thirds above",  minutes: 4, accent: "rose",    detail: "Pivot moves to the bottom. Thirds float to the right. Reverse the geography." },
              { title: "Thumb on fifth + free improv", minutes: 4, accent: "emerald", detail: "Last anchor. Once it's clean, pick any third pair you fancy and improvise — it's already music." },
            ]}
          />
        </Drill>
      </FadeIn>
    </>
  );
}

/* ── Bespoke visual: triplet cycle, three slots ── */
function TripletCycle({
  step,
  pivotFinger,
  pair,
  pivotDegree,
  nameForPc,
  tonicPc,
}: {
  step: number;
  pivotFinger: PivotFinger;
  pair?: { lower: number; upper: number; type: "Major" | "Minor" };
  pivotDegree: PivotDegree;
  nameForPc: (pc: number) => string;
  tonicPc: number;
}) {
  // Determine the three slot contents (pivot is at slot 2 for pinky-pivot, slot 0 for thumb-pivot)
  const pivotSemis = pivotDegree === 1 ? 0 : 7;
  const pivotPc = ((tonicPc + pivotSemis) % 12 + 12) % 12;
  const pivotName = nameForPc(pivotPc);
  const pairLowName = pair ? nameForPc(((tonicPc + pair.lower) % 12 + 12) % 12) : "—";
  const pairUpName  = pair ? nameForPc(((tonicPc + pair.upper) % 12 + 12) % 12) : "—";

  const slots: { label: "L" | "M" | "H"; note: string; isPivot: boolean }[] = pivotFinger === "pinky"
    ? [
        { label: "L", note: pairLowName, isPivot: false },
        { label: "M", note: pairUpName,  isPivot: false },
        { label: "H", note: pivotName,   isPivot: true },
      ]
    : [
        { label: "L", note: pivotName,   isPivot: true },
        { label: "M", note: pairLowName, isPivot: false },
        { label: "H", note: pairUpName,  isPivot: false },
      ];

  return (
    <div className="my-4 rounded-2xl border border-cyan-400/30 bg-zinc-900/40 p-4">
      <div className="mb-2 flex items-baseline justify-between">
        <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-amber-300">
          ◐ Triplet · L–M–H
        </div>
        <div className="font-mono text-[11px] text-cyan-300/80">
          {pivotFinger === "pinky" ? "pivot at H" : "pivot at L"} ·{" "}
          {pair?.type ?? "—"} third pair
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {slots.map((slot, i) => {
          const isNow = step === i;
          return (
            <motion.div
              key={i}
              animate={{ scale: isNow ? 1.06 : 1, opacity: isNow ? 1 : 0.7 }}
              transition={{ duration: 0.12 }}
              className={`flex flex-col items-center justify-center rounded-xl border p-4 ${
                slot.isPivot
                  ? "border-amber-300 bg-amber-500/15"
                  : isNow
                  ? "border-cyan-300 bg-cyan-500/20"
                  : "border-cyan-400/15 bg-zinc-900/30"
              }`}
            >
              <div className="font-mono text-[11px] uppercase tracking-wider text-cyan-300/80">
                slot {slot.label}
              </div>
              <div
                className={`mt-1 text-2xl font-bold ${
                  slot.isPivot ? "text-amber-200" : "text-cyan-100"
                }`}
              >
                {slot.note}
              </div>
              {slot.isPivot && (
                <div className="mt-1 text-[10px] uppercase tracking-wider text-amber-300/80">
                  ⛓ pivot
                </div>
              )}
            </motion.div>
          );
        })}
      </div>
      <p className="mt-2 text-[12px] italic text-cyan-300/70">
        Three notes per triplet. The amber slot is the pivot — your{" "}
        <strong>{pivotFinger}</strong> stays on it. The other two slots
        cycle through the seven diatonic third pairs of the scale.
      </p>
    </div>
  );
}

/* ── shared ── */
function StudyDeck({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="relative mb-4 rounded-2xl border border-cyan-400/30 bg-zinc-950/85 p-3 shadow-[0_8px_30px_rgba(0,0,0,0.5)]">
      <button onClick={() => setOpen((o) => !o)} className="flex w-full items-center justify-between text-left">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-amber-300">✦ Study deck</div>
          <div className="text-[12px] text-cyan-300/70">Key picker · master keyboard · pivot toggle</div>
        </div>
        <span className="text-[12px] font-mono text-cyan-300">{open ? "▴" : "▾"}</span>
      </button>
      <motion.div initial={false} animate={{ height: open ? "auto" : 0, opacity: open ? 1 : 0 }} transition={{ duration: 0.25 }} style={{ overflow: "hidden" }}>
        <div className="mt-3 space-y-3">{children}</div>
      </motion.div>
    </div>
  );
}

function FadeIn({ children }: { children: ReactNode }) {
  const reduced = useReducedMotion();
  return (
    <motion.div initial={reduced ? false : { opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55, ease: "easeOut" }}>
      {children}
    </motion.div>
  );
}

function PIQuote({ children, attribution }: { children: ReactNode; attribution?: string }) {
  return (
    <figure className="my-4 rounded-2xl border-l-4 border-amber-400/80 bg-zinc-900/60 px-5 py-4">
      <blockquote className="text-[17px] leading-relaxed text-cyan-50/95" style={{ fontFamily: '"Cormorant Garamond", serif', fontStyle: "italic" }}>
        {children}
      </blockquote>
      {attribution && <figcaption className="mt-2 text-[11px] uppercase tracking-[0.18em] text-amber-300/80">— {attribution}</figcaption>}
    </figure>
  );
}
